import React, { useState, useMemo } from 'react';
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { auditService, AuditLog } from '../services/auditService';
import { loginActivityService, LoginActivity } from '../services/loginActivityService';
import { useAuth } from '../contexts/AuthContext';
import SettingsBackButton from '../components/common/SettingsBackButton';

const AuditPage: React.FC = () => {
  const { isSuperAdmin, hasPermission } = useAuth();
  const canViewAudit = isSuperAdmin || hasPermission('audit');
  const [activeView, setActiveView] = useState<'audit' | 'login'>('audit');
  const [filters, setFilters] = useState({
    entity: '',
    action: '',
    limit: 10,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loginSearch, setLoginSearch] = useState('');
  const [loginPortal, setLoginPortal] = useState('');
  const [loginSuccess, setLoginSuccess] = useState<string>('');
  const [loginRefreshing, setLoginRefreshing] = useState(false);

  const { data: auditData, isLoading, error, refetch } = useQuery({
    queryKey: ['audit-logs', filters],
    queryFn: () => auditService.getAuditLogs(filters),
    enabled: canViewAudit && activeView === 'audit',
  });

  const { data: loginData, isLoading: loginLoading, error: loginError, refetch: refetchLogin } = useQuery({
    queryKey: ['login-activity', loginSearch, loginPortal, loginSuccess],
    queryFn: () =>
      loginActivityService.getLoginActivity({
        limit: 50,
        search: loginSearch || undefined,
        portal: loginPortal || undefined,
        success: loginSuccess === '' ? undefined : loginSuccess === 'true',
      }),
    enabled: canViewAudit && activeView === 'login',
  });

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setTimeout(() => setRefreshing(false), 600);
  };

  const handleLoginRefresh = async () => {
    setLoginRefreshing(true);
    await refetchLogin();
    setTimeout(() => setLoginRefreshing(false), 600);
  };

  const exportLoginCSV = () => {
    const rows = loginRows;
    const csvRows = [['Date', 'Email', 'User', 'Role', 'Portal', 'Status', 'IP', 'Location', 'Device']];
    rows.forEach((row) => {
      csvRows.push([
        row.createdAt,
        row.email,
        loginActivityService.formatUserName(row),
        row.userRole || row.user?.role || '',
        loginActivityService.formatPortal(row.portal),
        row.success ? 'Success' : 'Failed',
        row.ipAddress || '',
        loginActivityService.formatLocation(row),
        loginActivityService.formatDevice(row),
      ]);
    });
    const csv = csvRows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'login-activity.csv';
    link.click();
  };

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const openDetailModal = (log: AuditLog) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedLog(null);
  };

  const exportCSV = () => {
    const logs = filteredLogs;
    const rows = [['ID', 'Admin', 'Action', 'Entity', 'Entity ID', 'Date', 'IP', 'Browser']];
    logs.forEach(log => {
      rows.push([
        log.id,
        auditService.formatUserName(log.user),
        log.action,
        log.entity,
        log.entityId || '',
        log.createdAt,
        log.ipAddress || '',
        (log.userAgent || '').substring(0, 60)
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'audit-logs.csv';
    link.click();
  };

  const getActionIconClass = (action: string): string => {
    switch (action) {
      case 'CREATE': return 'ai-created';
      case 'UPDATE': return 'ai-updated';
      case 'DELETE': return 'ai-deleted';
      case 'RESTORE': return 'ai-restored';
      case 'ACCEPT':
      case 'BULK_ACCEPT': return 'ai-created';
      case 'REJECT': return 'ai-deleted';
      default: return 'ai-other';
    }
  };

  const getActionIconSVG = (action: string): string => {
    switch (action) {
      case 'CREATE':
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
      case 'UPDATE':
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>';
      case 'DELETE':
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>';
      case 'RESTORE':
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/></svg>';
      case 'ACCEPT':
      case 'BULK_ACCEPT':
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
      case 'REJECT':
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
      default:
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';
    }
  };

  const getActionBadgeClass = (action: string): string => {
    switch (action) {
      case 'CREATE': return 'ab-created';
      case 'UPDATE': return 'ab-updated';
      case 'DELETE': return 'ab-deleted';
      case 'RESTORE': return 'ab-restored';
      case 'ACCEPT':
      case 'BULK_ACCEPT': return 'ab-created';
      case 'REJECT': return 'ab-deleted';
      default: return 'ab-other';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const groupByDay = (logs: AuditLog[]) => {
    const now = new Date();
    const today = now.toDateString();
    const yesterday = new Date(now.getTime() - 86400000).toDateString();
    const groups: { [key: string]: AuditLog[] } = {};
    
    logs.forEach(log => {
      const logDate = new Date(log.createdAt).toDateString();
      const key = logDate === today ? 'Today' : logDate === yesterday ? 'Yesterday' : 'Older';
      if (!groups[key]) groups[key] = [];
      groups[key].push(log);
    });
    
    return groups;
  };

  const auditLogs = auditData?.data || [];
  const totalActivities = auditData?.pagination?.total || 0;
  const loginRows = loginData?.data || [];
  const totalLogins = loginData?.pagination?.total || 0;
  const successfulLogins = loginRows.filter((r) => r.success).length;
  const failedLogins = loginRows.filter((r) => !r.success).length;

  // Filter logs by search query
  const filteredLogs = useMemo(() => {
    if (!searchQuery) return auditLogs;
    const q = searchQuery.toLowerCase();
    return auditLogs.filter(log => {
      const userName = auditService.formatUserName(log.user).toLowerCase();
      const entity = log.entity.toLowerCase();
      const action = log.action.toLowerCase();
      return userName.includes(q) || entity.includes(q) || action.includes(q);
    });
  }, [auditLogs, searchQuery]);

  const clientActivities = auditLogs.filter(log => log.entity === 'Client').length;
  const orderActivities = auditLogs.filter(log => log.entity === 'GuestBlogOrder').length;
  const groupedLogs = groupByDay(filteredLogs);

  if (!canViewAudit) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert severity="error">
          Access denied. You do not have permission to view audit logs.
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ padding: '22px', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        :root {
          --surface: #FFFFFF;
          --surface-2: #F9FAFB;
          --surface-3: #FAFAFA;
          --border: #E5E7EB;
          --border-sub: #F3F4F6;
          --border-strong: #D1D5DB;
          --t1: #111827;
          --t2: #374151;
          --t3: #6B7280;
          --t4: #9CA3AF;
          --t5: #D1D5DB;
          --blue: #4F46E5;
          --blue-h: #4338CA;
          --blue-l: #EEF2FF;
          --blue-b: #C7D2FE;
          --green: #059669;
          --green-h: #047857;
          --green-l: #ECFDF5;
          --green-b: #A7F3D0;
          --amber: #D97706;
          --amber-h: #B45309;
          --amber-l: #FFFBEB;
          --amber-b: #FDE68A;
          --red: #DC2626;
          --red-h: #B91C1C;
          --red-l: #FEF2F2;
          --red-b: #FECACA;
          --purple: #7C3AED;
          --purple-l: #F5F3FF;
          --purple-b: #DDD6FE;
          --orange: #EA580C;
          --orange-l: #FFF7ED;
          --teal: #0D9488;
          --teal-l: #F0FDFA;
          --teal-b: #99F6E4;
          --r: 6px;
          --rl: 8px;
          --rxl: 12px;
          --sh-xs: 0 1px 2px rgba(0,0,0,.05);
          --sh-sm: 0 1px 3px rgba(0,0,0,.07), 0 1px 2px rgba(0,0,0,.04);
          --sh-md: 0 4px 12px rgba(0,0,0,.07), 0 2px 4px rgba(0,0,0,.04);
        }

        .audit-page-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .ph {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }

        .ph-title {
          font-size: 19px;
          font-weight: 700;
          letter-spacing: -0.4px;
          color: var(--t1);
          margin: 0;
        }

        .ph-sub {
          font-size: 13px;
          color: var(--t3);
          margin-top: 3px;
        }

        .stats-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .stat-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--rl);
          padding: 16px 18px;
          box-shadow: var(--sh-xs);
          display: flex;
          align-items: center;
          gap: 14px;
          transition: box-shadow 0.18s, transform 0.18s;
          cursor: default;
        }

        .stat-card:hover {
          box-shadow: var(--sh-md);
          transform: translateY(-1px);
        }

        .stat-ico {
          width: 42px;
          height: 42px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-ico svg {
          width: 19px;
          height: 19px;
        }

        .sic-blue {
          background: var(--blue-l);
          border: 1px solid var(--blue-b);
        }

        .sic-blue svg {
          color: var(--blue);
        }

        .sic-green {
          background: var(--green-l);
          border: 1px solid var(--green-b);
        }

        .sic-green svg {
          color: var(--green);
        }

        .sic-teal {
          background: var(--teal-l);
          border: 1px solid var(--teal-b);
        }

        .sic-teal svg {
          color: var(--teal);
        }

        .stat-val {
          font-size: 24px;
          font-weight: 700;
          letter-spacing: -0.5px;
          color: var(--t1);
          font-family: 'DM Mono', monospace;
          line-height: 1;
        }

        .stat-lbl {
          font-size: 12px;
          color: var(--t3);
          margin-top: 3px;
        }

        .filter-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--rl);
          box-shadow: var(--sh-xs);
          padding: 12px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .fsel {
          appearance: none;
          border: 1px solid var(--border);
          border-radius: var(--r);
          padding: 7px 28px 7px 11px;
          font-family: inherit;
          font-size: 12.5px;
          color: var(--t2);
          background: var(--surface-2);
          outline: none;
          cursor: pointer;
          transition: all 0.14s;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 8px center;
          min-width: 155px;
        }

        .fsel:focus {
          border-color: var(--blue);
          background-color: var(--surface);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.08);
        }

        .fsearch {
          display: flex;
          align-items: center;
          gap: 7px;
          background: var(--surface-2);
          border: 1px solid var(--border);
          border-radius: var(--r);
          padding: 6px 10px;
          min-width: 210px;
          transition: all 0.15s;
        }

        .fsearch:focus-within {
          background: var(--surface);
          border-color: var(--blue);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.08);
        }

        .fsearch svg {
          width: 13px;
          height: 13px;
          color: var(--t4);
          flex-shrink: 0;
        }

        .fsearch input {
          border: none;
          background: none;
          outline: none;
          font-family: inherit;
          font-size: 12.5px;
          color: var(--t1);
          width: 100%;
        }

        .fsearch input::placeholder {
          color: var(--t4);
        }

        .fsp {
          flex: 1;
        }

        .refresh-btn, .export-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 13px;
          border-radius: var(--r);
          border: 1px solid var(--border);
          background: var(--surface);
          font-family: inherit;
          font-size: 12.5px;
          font-weight: 500;
          color: var(--t2);
          cursor: pointer;
          box-shadow: var(--sh-xs);
          transition: all 0.14s;
          white-space: nowrap;
        }

        .refresh-btn:hover, .export-btn:hover {
          background: var(--surface-2);
          color: var(--t1);
        }

        .refresh-btn svg {
          width: 13px;
          height: 13px;
          color: var(--blue);
        }

        .export-btn svg {
          width: 13px;
          height: 13px;
          color: var(--t3);
        }

        .refresh-btn.spinning svg {
          animation: spin 0.6s linear 1;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .feed-wrap {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--rl);
          box-shadow: var(--sh-xs);
          overflow: hidden;
          animation: fadeUp 0.22s ease forwards;
        }

        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .feed-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 18px;
          border-bottom: 1px solid var(--border-sub);
          background: var(--surface-3);
        }

        .feed-title {
          font-size: 13px;
          font-weight: 600;
          color: var(--t1);
        }

        .feed-count {
          font-size: 12px;
          color: var(--t4);
        }

        .feed-group-label {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 20px 8px;
        }

        .fgl-text {
          font-size: 11px;
          font-weight: 700;
          color: var(--t4);
          text-transform: uppercase;
          letter-spacing: 0.6px;
          white-space: nowrap;
        }

        .fgl-line {
          flex: 1;
          height: 1px;
          background: var(--border-sub);
        }

        .timeline {
          padding: 0 20px 10px;
          position: relative;
        }

        .timeline::before {
          content: '';
          position: absolute;
          left: 40px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: var(--border-sub);
          pointer-events: none;
        }

        .act-item {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 10px 8px;
          position: relative;
          cursor: pointer;
          transition: background 0.1s;
          border-radius: var(--r);
          margin: 0 -8px;
        }

        .act-item:hover {
          background: var(--surface-2);
        }

        .act-item + .act-item {
          border-top: 1px solid var(--border-sub);
          margin-top: 0;
        }

        .act-ico {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          position: relative;
          z-index: 1;
          box-shadow: 0 0 0 3px var(--surface);
        }

        .act-ico svg {
          width: 15px;
          height: 15px;
        }

        .ai-created {
          background: var(--green);
          color: #fff;
        }

        .ai-updated {
          background: var(--amber);
          color: #fff;
        }

        .ai-deleted {
          background: var(--red);
          color: #fff;
        }

        .ai-restored {
          background: var(--teal);
          color: #fff;
        }

        .ai-other {
          background: var(--blue);
          color: #fff;
        }

        .act-content {
          flex: 1;
          min-width: 0;
        }

        .act-main {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .act-text {
          font-size: 13px;
          font-weight: 500;
          color: var(--t1);
          line-height: 1.4;
        }

        .act-meta {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 4px;
          flex-wrap: wrap;
        }

        .act-entity {
          font-size: 11.5px;
          color: var(--blue);
          font-weight: 500;
        }

        .act-dot {
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: var(--t5);
          flex-shrink: 0;
        }

        .act-time {
          font-size: 11.5px;
          color: var(--t4);
        }

        .act-details {
          margin-top: 5px;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .act-ip {
          font-size: 11px;
          color: var(--t4);
          font-family: 'DM Mono', monospace;
        }

        .act-browser {
          font-size: 11px;
          color: var(--t5);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 480px;
        }

        .act-expand {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.25s ease;
        }

        .act-item.expanded .act-expand {
          max-height: 200px;
        }

        .act-expand-inner {
          margin-top: 8px;
          padding: 10px 12px;
          background: var(--surface-2);
          border: 1px solid var(--border-sub);
          border-radius: var(--r);
        }

        .exp-row {
          display: flex;
          gap: 8px;
          margin-bottom: 4px;
        }

        .exp-row:last-child {
          margin-bottom: 0;
        }

        .exp-label {
          font-size: 11px;
          font-weight: 600;
          color: var(--t4);
          min-width: 70px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .exp-val {
          font-size: 11.5px;
          color: var(--t2);
          font-family: 'DM Mono', monospace;
          word-break: break-all;
        }

        .act-badge {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          padding: 2px 8px;
          border-radius: 99px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.2px;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .ab-created {
          background: var(--green-l);
          color: var(--green-h);
          border: 1px solid var(--green-b);
        }

        .ab-updated {
          background: var(--amber-l);
          color: var(--amber-h);
          border: 1px solid var(--amber-b);
        }

        .ab-deleted {
          background: var(--red-l);
          color: var(--red-h);
          border: 1px solid var(--red-b);
        }

        .ab-restored {
          background: var(--teal-l);
          color: var(--teal);
          border: 1px solid var(--teal-b);
        }

        .ab-other {
          background: var(--blue-l);
          color: var(--blue);
          border: 1px solid var(--blue-b);
        }

        .act-chevron {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--t5);
          flex-shrink: 0;
          margin-top: 7px;
          transition: transform 0.2s;
        }

        .act-chevron svg {
          width: 12px;
          height: 12px;
        }

        .act-item.expanded .act-chevron {
          transform: rotate(180deg);
        }

        .view-details-btn {
          font-size: 11px;
          color: var(--blue);
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
          padding: 2px 0;
          text-decoration: none;
        }

        .view-details-btn:hover {
          text-decoration: underline;
        }

        .empty-feed {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 64px 24px;
          gap: 12px;
          text-align: center;
        }

        .empty-ico {
          width: 56px;
          height: 56px;
          background: var(--surface-2);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--border);
          margin-bottom: 4px;
        }

        .empty-ico svg {
          width: 24px;
          height: 24px;
          color: var(--t5);
        }

        .empty-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--t1);
          letter-spacing: -0.2px;
        }

        .empty-sub {
          font-size: 13px;
          color: var(--t3);
          max-width: 280px;
          line-height: 1.65;
        }

        .audit-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 4px;
        }

        .audit-tab {
          padding: 8px 16px;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: var(--surface);
          color: var(--t3);
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
        }

        .audit-tab.on {
          background: var(--accent-soft, #eef2ff);
          border-color: #c7d2fe;
          color: var(--accent, #2563eb);
        }

        .login-table-wrap {
          overflow-x: auto;
        }

        .login-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12.5px;
        }

        .login-table th {
          text-align: left;
          padding: 10px 12px;
          background: #eef2ff;
          color: #4338ca;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          border-bottom: 2px solid #c7d2fe;
          white-space: nowrap;
        }

        .login-table td {
          padding: 11px 12px;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: top;
          color: var(--t2);
        }

        .login-table tr:hover td {
          background: #f8fafc;
        }

        .login-status-ok {
          color: #16a34a;
          font-weight: 600;
        }

        .login-status-fail {
          color: #dc2626;
          font-weight: 600;
        }
      `}</style>

      <div className="audit-page-container">
        <SettingsBackButton />
        {/* Page Header */}
        <div className="ph">
          <div>
            <h1 className="ph-title">Admin Activity Audit</h1>
            <p className="ph-sub">Track admin actions and user login activity across the system</p>
          </div>
        </div>

        <div className="audit-tabs">
          <button
            type="button"
            className={`audit-tab ${activeView === 'audit' ? 'on' : ''}`}
            onClick={() => setActiveView('audit')}
          >
            System Activity
          </button>
          <button
            type="button"
            className={`audit-tab ${activeView === 'login' ? 'on' : ''}`}
            onClick={() => setActiveView('login')}
          >
            Login Activity
          </button>
        </div>

        {activeView === 'audit' && (
        <>
        {/* Stats Cards */}
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-ico sic-blue">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div>
              <div className="stat-val">{totalActivities}</div>
              <div className="stat-lbl">Total Activities</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-ico sic-green">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
              </svg>
            </div>
            <div>
              <div className="stat-val">{clientActivities}</div>
              <div className="stat-lbl">Client Activities</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-ico sic-teal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
            </div>
            <div>
              <div className="stat-val">{orderActivities}</div>
              <div className="stat-lbl">Order Activities</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filter-card">
          <div className="fsearch">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search activities…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="fsel"
            value={filters.entity}
            onChange={(e) => handleFilterChange('entity', e.target.value)}
          >
            <option value="">Entity Type</option>
            <option value="Client">Client</option>
            <option value="GuestBlogOrder">Order</option>
            <option value="GuestBlogPlacement">Placement</option>
            <option value="Project">Project</option>
            <option value="User">User</option>
            <option value="ClientAssignment">Client Assignment</option>
            <option value="Publisher">Publisher</option>
            <option value="GuestBlogSite">Guest Blog Site</option>
            <option value="LiveLinkStatus">Live Link</option>
            <option value="Invoice">Invoice</option>
            <option value="Category">Category</option>
            <option value="ClientPlan">Client Plan</option>
            <option value="PendingGuestBlogSite">Pending Site</option>
          </select>
          <select
            className="fsel"
            value={filters.action}
            onChange={(e) => handleFilterChange('action', e.target.value)}
          >
            <option value="">Action</option>
            <option value="CREATE">created</option>
            <option value="UPDATE">updated</option>
            <option value="DELETE">deleted</option>
            <option value="RESTORE">restored</option>
            <option value="ACCEPT">accepted</option>
            <option value="REJECT">rejected</option>
            <option value="BULK_ACCEPT">bulk accepted</option>
          </select>
          <select
            className="fsel"
            value={filters.limit}
            onChange={(e) => handleFilterChange('limit', Number(e.target.value))}
          >
            <option value={10}>Last 10</option>
            <option value={25}>Last 25</option>
            <option value={50}>Last 50</option>
            <option value={100}>Last 100</option>
          </select>
          <div className="fsp"></div>
          <button className="export-btn" onClick={exportCSV}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export CSV
          </button>
          <button className={`refresh-btn ${refreshing ? 'spinning' : ''}`} onClick={handleRefresh}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            Refresh
          </button>
        </div>

        {/* Activity Feed */}
        <div className="feed-wrap">
          <div className="feed-header">
            <span className="feed-title">Recent Activities</span>
            <span className="feed-count">{filteredLogs.length} {filteredLogs.length === 1 ? 'entry' : 'entries'}</span>
          </div>

          {isLoading ? (
            <div className="empty-feed">
              <div className="empty-ico">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="empty-title">Loading...</div>
              <div className="empty-sub">Please wait while we fetch the audit logs.</div>
            </div>
          ) : error ? (
            <div style={{ padding: '16px' }}>
              <Alert severity="error">
                Failed to load audit logs. Please try again.
              </Alert>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="empty-feed">
              <div className="empty-ico">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="empty-title">No activity found</div>
              <div className="empty-sub">No logs match your current filters. Try adjusting entity type, action, or search terms.</div>
            </div>
          ) : (
            <>
              {['Today', 'Yesterday', 'Older'].map(groupKey => {
                if (!groupedLogs[groupKey] || groupedLogs[groupKey].length === 0) return null;
                return (
                  <React.Fragment key={groupKey}>
                    <div className="feed-group-label">
                      <span className="fgl-text">{groupKey}</span>
                      <span className="fgl-line"></span>
                    </div>
                    <div className="timeline">
                      {groupedLogs[groupKey].map((log: AuditLog) => {
                        const isExpanded = expandedItems.has(log.id);
                        const hasDetails = log.oldValues || log.newValues;
                        return (
                          <div
                            key={log.id}
                            className={`act-item ${isExpanded ? 'expanded' : ''}`}
                            onClick={() => hasDetails && toggleExpand(log.id)}
                          >
                            <div
                              className={`act-ico ${getActionIconClass(log.action)}`}
                              dangerouslySetInnerHTML={{ __html: getActionIconSVG(log.action) }}
                            />
                            <div className="act-content">
                              <div className="act-main">
                                <span className="act-text">{auditService.formatActivityDescription(log)}</span>
                                <span className={`act-badge ${getActionBadgeClass(log.action)}`}>
                                  {auditService.formatActionName(log.action)}
                                </span>
                              </div>
                              <div className="act-meta">
                                <span className="act-entity">{auditService.formatEntityName(log.entity)}</span>
                                <span className="act-dot"></span>
                                <span className="act-entity" style={{ color: 'var(--t4)', fontWeight: 400 }}>
                                  {log.entityId || 'N/A'}
                                </span>
                                <span className="act-dot"></span>
                                <span className="act-time">{formatTimeAgo(log.createdAt)}</span>
                              </div>
                              <div className="act-details">
                                {log.ipAddress && <span className="act-ip">IP: {log.ipAddress}</span>}
                                {log.userAgent && <span className="act-browser">{log.userAgent}</span>}
                              </div>
                              {hasDetails && (
                                <div className="act-expand">
                                  <div className="act-expand-inner">
                                    {log.newValues && (() => {
                                      try {
                                        const parsedValues = typeof log.newValues === 'string' 
                                          ? JSON.parse(log.newValues) 
                                          : log.newValues;
                                        return Object.entries(parsedValues).map(([key, value]) => (
                                          <div key={key} className="exp-row">
                                            <span className="exp-label">{key}</span>
                                            <span className="exp-val">
                                              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                            </span>
                                          </div>
                                        ));
                                      } catch (e) {
                                        return (
                                          <div className="exp-row">
                                            <span className="exp-label">Data</span>
                                            <span className="exp-val">{String(log.newValues)}</span>
                                          </div>
                                        );
                                      }
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
                              <button
                                className="view-details-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDetailModal(log);
                                }}
                              >
                                View details
                              </button>
                            </div>
                            {hasDetails && (
                              <div className="act-chevron">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="6 9 12 15 18 9"/>
                                </svg>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </React.Fragment>
                );
              })}
            </>
          )}
        </div>
        </>
        )}

        {activeView === 'login' && (
          <>
            <div className="stats-row">
              <div className="stat-card">
                <div className="stat-ico sic-blue">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                    <polyline points="10 17 15 12 10 7"/>
                    <line x1="15" y1="12" x2="3" y2="12"/>
                  </svg>
                </div>
                <div>
                  <div className="stat-val">{totalLogins}</div>
                  <div className="stat-lbl">Login Events</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-ico sic-green">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div>
                  <div className="stat-val">{successfulLogins}</div>
                  <div className="stat-lbl">Successful (page)</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-ico sic-teal">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </div>
                <div>
                  <div className="stat-val">{failedLogins}</div>
                  <div className="stat-lbl">Failed (page)</div>
                </div>
              </div>
            </div>

            <div className="filter-card">
              <div className="fsearch">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search email, IP, city, browser…"
                  value={loginSearch}
                  onChange={(e) => setLoginSearch(e.target.value)}
                />
              </div>
              <select className="fsel" value={loginPortal} onChange={(e) => setLoginPortal(e.target.value)}>
                <option value="">All portals</option>
                <option value="admin">Administrator</option>
                <option value="publisher">Publisher</option>
                <option value="portal">Client / Agency</option>
                <option value="client">Client</option>
                <option value="agency">Agency</option>
              </select>
              <select className="fsel" value={loginSuccess} onChange={(e) => setLoginSuccess(e.target.value)}>
                <option value="">All results</option>
                <option value="true">Successful only</option>
                <option value="false">Failed only</option>
              </select>
              <div className="fsp"></div>
              <button className="export-btn" onClick={exportLoginCSV}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export CSV
              </button>
              <button className={`refresh-btn ${loginRefreshing ? 'spinning' : ''}`} onClick={handleLoginRefresh}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/>
                  <polyline points="1 20 1 14 7 14"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
                Refresh
              </button>
            </div>

            <div className="feed-wrap">
              <div className="feed-header">
                <span className="feed-title">Login Activity</span>
                <span className="feed-count">{loginRows.length} shown</span>
              </div>

              {loginLoading ? (
                <div className="empty-feed">
                  <div className="empty-title">Loading login activity…</div>
                </div>
              ) : loginError ? (
                <div style={{ padding: '16px' }}>
                  <Alert severity="error">Failed to load login activity.</Alert>
                </div>
              ) : loginRows.length === 0 ? (
                <div className="empty-feed">
                  <div className="empty-title">No login events yet</div>
                  <div className="empty-sub">Successful and failed sign-in attempts will appear here after users log in.</div>
                </div>
              ) : (
                <div className="login-table-wrap">
                  <table className="login-table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Email</th>
                        <th>User</th>
                        <th>Portal</th>
                        <th>Status</th>
                        <th>IP</th>
                        <th>Location</th>
                        <th>Device / Browser</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loginRows.map((row: LoginActivity) => (
                        <tr key={row.id}>
                          <td>{new Date(row.createdAt).toLocaleString()}</td>
                          <td>{row.email}</td>
                          <td>{loginActivityService.formatUserName(row)}</td>
                          <td>{loginActivityService.formatPortal(row.portal)}</td>
                          <td className={row.success ? 'login-status-ok' : 'login-status-fail'}>
                            {row.success ? 'Success' : 'Failed'}
                          </td>
                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: '11.5px' }}>
                            {row.ipAddress || '—'}
                          </td>
                          <td>{loginActivityService.formatLocation(row)}</td>
                          <td>{loginActivityService.formatDevice(row)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onClose={closeDetailModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--t1)', letterSpacing: '-0.25px' }}>
                {selectedLog ? `${auditService.formatEntityName(selectedLog.entity)} — ${auditService.formatActionName(selectedLog.action)}` : 'Activity Details'}
              </div>
              <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '3px' }}>
                {selectedLog ? `${auditService.formatUserName(selectedLog.user)} · ${new Date(selectedLog.createdAt).toLocaleString()}` : '—'}
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', paddingTop: '8px' }}>
              <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-sub)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>Admin</div>
                <div style={{ fontSize: '12.5px', color: 'var(--t1)', fontWeight: 500 }}>{auditService.formatUserName(selectedLog.user)}</div>
              </div>
              <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-sub)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>Action</div>
                <div style={{ fontSize: '12.5px', color: 'var(--t1)', fontWeight: 500 }}>{selectedLog.action}</div>
              </div>
              <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-sub)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>Entity</div>
                <div style={{ fontSize: '12.5px', color: 'var(--t1)', fontWeight: 500 }}>{selectedLog.entity}</div>
              </div>
              <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-sub)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>Entity ID</div>
                <div style={{ fontSize: '12.5px', color: 'var(--t1)', fontFamily: "'DM Mono', monospace" }}>{selectedLog.entityId || 'N/A'}</div>
              </div>
              {selectedLog.ipAddress && (
                <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-sub)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>IP Address</div>
                  <div style={{ fontSize: '12.5px', color: 'var(--t1)', fontFamily: "'DM Mono', monospace" }}>{selectedLog.ipAddress}</div>
                </div>
              )}
              <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-sub)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>Timestamp</div>
                <div style={{ fontSize: '12.5px', color: 'var(--t1)', fontFamily: "'DM Mono', monospace" }}>{new Date(selectedLog.createdAt).toLocaleString()}</div>
              </div>
              {selectedLog.userAgent && (
                <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-sub)', borderRadius: 'var(--r)', padding: '10px 12px', gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>Browser</div>
                  <div style={{ fontSize: '11px', color: 'var(--t1)', fontFamily: "'DM Mono', monospace", lineHeight: '1.5' }}>{selectedLog.userAgent}</div>
                </div>
              )}
              {selectedLog.newValues && (() => {
                try {
                  const parsedValues = typeof selectedLog.newValues === 'string' 
                    ? JSON.parse(selectedLog.newValues) 
                    : selectedLog.newValues;
                  return Object.entries(parsedValues).map(([key, value]) => (
                    <div key={key} style={{ background: 'var(--surface-2)', border: '1px solid var(--border-sub)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>{key}</div>
                      <div style={{ fontSize: '12.5px', color: 'var(--t1)', fontWeight: 500 }}>
                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                      </div>
                    </div>
                  ));
                } catch (e) {
                  return (
                    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-sub)', borderRadius: 'var(--r)', padding: '10px 12px', gridColumn: '1 / -1' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>Data</div>
                      <div style={{ fontSize: '11px', color: 'var(--t1)', fontFamily: "'DM Mono', monospace", lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                        {String(selectedLog.newValues)}
                      </div>
                    </div>
                  );
                }
              })()}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailModal}>Close</Button>
        </DialogActions>
      </Dialog>
      </div>
    </div>
  );
};

export default AuditPage;
